import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, GanttChartSquare, ZoomIn, ZoomOut, FileText, FileImage, FileSpreadsheet, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Rows3, X, Upload, ArrowLeft, MapPin, Pencil, FolderOpen, CalendarDays } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const WD = ['D', 'L', 'M', 'M', 'G', 'V', 'S'];
const COLORS = ['#ea7a1d', '#f5c518', '#1a4b8c', '#15803d', '#b91c1c', '#7c3aed', '#0e7490', '#475569'];
const DAY = 86400000;
const NOW_YEAR = new Date().getFullYear();
const toISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('it-IT') : '-';

const emptyTaskForm = () => ({ name: '', category: '', section_id: '', progress: 0, num_workers: 0, color: COLORS[0], notes: '', fillStartMode: 'date', fillStart: '', fillMode: 'durata', fillValue: '' });
const bulkRow = (i = 0) => ({ name: '', color: COLORS[i % COLORS.length], num_workers: 0, start: '', mode: 'durata', value: '' });
const emptyChartForm = () => ({ name: '', luogo: '', project_id: '__none__', date_mode: 'date', work_saturday: true, work_sunday: false, year: NOW_YEAR });

export default function GanttPage() {
  const { isRole } = useAuth();
  const canManage = isRole('admin', 'aspp', 'qhse', 'capo_cantiere');

  // ---- elenco cronoprogrammi ----
  const [charts, setCharts] = useState([]);
  const [chartsLoading, setChartsLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);

  // dialog crea/modifica chart + import
  const [chartDialog, setChartDialog] = useState(null); // { mode:'new'|'edit'|'import', form, file }
  const [chartSaving, setChartSaving] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const importFileRef = useRef(null);

  // ---- dettaglio chart selezionato ----
  const selectedChart = useMemo(() => charts.find(c => c.id === selectedId) || null, [charts, selectedId]);
  const [tasks, setTasks] = useState([]);
  const [tasksLoading, setTasksLoading] = useState(false);
  const [cellW, setCellW] = useState(30);
  const [descW, setDescW] = useState(() => {
    const saved = Number(localStorage.getItem('gantt_desc_w'));
    return saved && saved >= 150 && saved <= 640 ? saved : 220;
  });
  useEffect(() => { try { localStorage.setItem('gantt_desc_w', String(descW)); } catch (e) {} }, [descW]);

  // impostazioni di visualizzazione: derivano dal chart
  const dateMode = selectedChart?.date_mode || 'date';
  const workSaturday = selectedChart?.work_saturday ?? true;
  const workSunday = selectedChart?.work_sunday ?? false;
  const showWorkers = selectedChart?.show_workers ?? false;
  const margin = selectedChart?.margin ?? 0;
  const sections = selectedChart?.sections || [];

  const today = new Date();
  const [periodStart, setPeriodStart] = useState(toISO(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [numDays, setNumDays] = useState(new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate());

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyTaskForm());
  const [exporting, setExporting] = useState(false);

  const scrollRef = useRef(null);
  const ganttRef = useRef(null);
  const tasksRef = useRef(tasks);
  const paintRef = useRef({ active: false, taskId: null, mode: 'add' });
  useEffect(() => { tasksRef.current = tasks; }, [tasks]);

  const isWorkingDay = useCallback((wd) => (wd !== 0 || workSunday) && (wd !== 6 || workSaturday), [workSaturday, workSunday]);

  // lettere dei giorni della settimana lavorativi (Lun..Dom) in modalità "giornate lavorative"
  const orderedWeekdays = useMemo(() => {
    const base = ['L', 'M', 'M', 'G', 'V'];
    if (workSaturday) base.push('S');
    if (workSunday) base.push('D');
    return base;
  }, [workSaturday, workSunday]);
  const daysPerWeek = Math.max(1, orderedWeekdays.length);

  const nthWorkingDay = useCallback((n) => {
    const idx = parseInt(n, 10) || 0;
    if (!periodStart || idx < 1) return '';
    let cur = new Date(periodStart + 'T00:00:00');
    let count = 0, guard = 0;
    while (guard < 3000) {
      if (isWorkingDay(cur.getDay())) { count++; if (count === idx) return toISO(cur); }
      cur = new Date(cur.getTime() + DAY); guard++;
    }
    return '';
  }, [periodStart, isWorkingDay]);

  const fillDays = useCallback((startISO, mode, value) => {
    if (!startISO) return [];
    const out = [];
    let cur = new Date(startISO + 'T00:00:00');
    let guard = 0;
    if (mode === 'datafine') {
      if (!value) return [];
      const end = new Date(value + 'T00:00:00');
      while (cur <= end && guard < 3000) { if (isWorkingDay(cur.getDay())) out.push(toISO(cur)); cur = new Date(cur.getTime() + DAY); guard++; }
    } else {
      const n = Math.max(0, parseInt(value, 10) || 0);
      while (out.length < n && guard < 3000) { if (isWorkingDay(cur.getDay())) out.push(toISO(cur)); cur = new Date(cur.getTime() + DAY); guard++; }
    }
    return out;
  }, [isWorkingDay]);

  // Inserimento rapido
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkRows, setBulkRows] = useState([bulkRow(0), bulkRow(1), bulkRow(2)]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkUseWorkday, setBulkUseWorkday] = useState(false);

  // Assegna date calendario
  const [applyDialog, setApplyDialog] = useState(false);
  const [pdfOptionsDialog, setPdfOptionsDialog] = useState(false);
  const [pdfLayout, setPdfLayout] = useState('multi');
  const [pdfPageSize, setPdfPageSize] = useState('a4');
  const [applyForm, setApplyForm] = useState({ start_date: '', end_date: '', work_saturday: false, work_sunday: false });
  const [applying, setApplying] = useState(false);

  // ---- data loading ----
  const loadCharts = useCallback(async () => {
    setChartsLoading(true);
    try {
      const res = await api.get('/gantt/charts');
      setCharts(res.data || []);
    } catch (e) { console.error(e); }
    finally { setChartsLoading(false); }
  }, []);

  const loadMeta = useCallback(async () => {
    try {
      const [p, e] = await Promise.all([api.get('/projects'), api.get('/employees/list/simple')]);
      setProjects(p.data || []); setEmployees(e.data || []);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { loadCharts(); loadMeta(); }, [loadCharts, loadMeta]);

  // inquadra periodo sulle attività del chart
  const frameToTasks = useCallback((list) => {
    const all = [];
    list.forEach(t => (t.marked_days || []).forEach(d => all.push(d)));
    if (all.length) {
      const min = all.reduce((a, b) => a < b ? a : b);
      const max = all.reduce((a, b) => a > b ? a : b);
      const dmin = new Date(min + 'T00:00:00');
      const dmax = new Date(max + 'T00:00:00');
      const startMonth = new Date(dmin.getFullYear(), dmin.getMonth(), 1);
      setPeriodStart(toISO(startMonth));
      const span = Math.round((dmax - startMonth) / DAY) + 2;
      setNumDays(Math.max(31, Math.min(180, span)));
    }
  }, []);

  const loadTasks = useCallback(async (chartId, frame = false, chart = null) => {
    if (!chartId) { setTasks([]); return; }
    setTasksLoading(true);
    try {
      const res = await api.get('/gantt/tasks', { params: { chart_id: chartId } });
      const list = (res.data || []).map(t => ({ ...t, marked_days: t.marked_days || [], num_workers: t.num_workers || 0 }));
      setTasks(list);
      if (frame) {
        if (chart?.start_date) {
          // allinea l'inizio periodo all'ancora del cronoprogramma (giorno 1)
          setPeriodStart(chart.start_date);
          const all = [];
          list.forEach(t => (t.marked_days || []).forEach(d => all.push(d)));
          if (all.length) {
            const max = all.reduce((a, b) => a > b ? a : b);
            const span = Math.round((new Date(max + 'T00:00:00') - new Date(chart.start_date + 'T00:00:00')) / DAY) + 2;
            setNumDays(Math.max(31, Math.min(180, span)));
          }
        } else {
          frameToTasks(list);
        }
      }
    } catch (e) { console.error(e); }
    finally { setTasksLoading(false); }
  }, [frameToTasks]);

  const openChart = (chart) => {
    setSelectedId(chart.id);
    loadTasks(chart.id, true, chart);
  };
  const backToList = () => { setSelectedId(null); setTasks([]); loadCharts(); };

  // patch impostazioni chart (ottimistico + PUT)
  const updateChartSettings = useCallback(async (patch) => {
    if (!selectedChart) return;
    setCharts(prev => prev.map(c => c.id === selectedChart.id ? { ...c, ...patch } : c));
    try { await api.put(`/gantt/charts/${selectedChart.id}`, patch); }
    catch (e) { console.error(e); }
  }, [selectedChart]);

  // lavora sabato/domenica: riadatta automaticamente le giornate di tutte le attività
  // (stesso numero di gg per attività, solo spostate secondo la nuova regola)
  const toggleWorkday = useCallback(async (patch) => {
    if (!selectedChart) return;
    const body = { work_saturday: workSaturday, work_sunday: workSunday, ...patch };
    setCharts(prev => prev.map(c => c.id === selectedChart.id ? { ...c, ...body } : c));
    try {
      await api.put(`/gantt/charts/${selectedChart.id}/workdays`, body);
      loadTasks(selectedChart.id);
    } catch (e) { console.error(e); alert('Errore aggiornamento calendario lavorativo'); }
  }, [selectedChart, workSaturday, workSunday, loadTasks]);

  // sezioni/fasi del cronoprogramma (registro colori come nei preventivi)
  const saveSections = useCallback(async (newSections) => {
    if (!selectedChart) return;
    setCharts(prev => prev.map(c => c.id === selectedChart.id ? { ...c, sections: newSections } : c));
    try { await api.put(`/gantt/charts/${selectedChart.id}`, { sections: newSections }); }
    catch (e) { console.error(e); }
  }, [selectedChart]);
  const [newSectionTitle, setNewSectionTitle] = useState('');
  const [showNewSection, setShowNewSection] = useState(false);
  const createSection = () => {
    const title = newSectionTitle.trim();
    if (!title) return;
    const color = COLORS[sections.length % COLORS.length];
    saveSections([...sections, { id: (crypto.randomUUID ? crypto.randomUUID() : `sec_${Date.now()}`), title, color }]);
    setNewSectionTitle(''); setShowNewSection(false);
  };
  const renameSection = (id, title) => saveSections(sections.map(s => s.id === id ? { ...s, title } : s));
  const deleteSection = (id) => { if (window.confirm('Eliminare questa sezione? Le attività già assegnate mantengono il colore attuale.')) saveSections(sections.filter(s => s.id !== id)); };

  // ---- colonne giorni ----
  const days = useMemo(() => {
    const start = new Date(periodStart + 'T00:00:00');
    const out = [];
    const n = Math.max(1, Math.min(180, Number(numDays) || 31));
    let workIdx = 0;
    for (let i = 0; i < n; i++) {
      const d = new Date(start.getTime() + i * DAY);
      const wd = d.getDay();
      const working = isWorkingDay(wd);
      if (dateMode === 'lavorative' && !working) continue;
      if (working) workIdx++;
      const lavIdx = workIdx - 1;
      out.push({
        iso: toISO(d), day: d.getDate(), wd, month: d.getMonth(), year: d.getFullYear(),
        working, workIdx: working ? workIdx : null,
        weekNum: working ? Math.floor(lavIdx / daysPerWeek) + 1 : null,
        lavLetter: working ? orderedWeekdays[lavIdx % daysPerWeek] : '',
      });
    }
    return out;
  }, [periodStart, numDays, dateMode, isWorkingDay, daysPerWeek, orderedWeekdays]);

  const monthSpans = useMemo(() => {
    const spans = [];
    if (dateMode === 'lavorative') {
      days.forEach(d => {
        const last = spans[spans.length - 1];
        if (last && last.weekNum === d.weekNum) last.count++;
        else spans.push({ key: `sett-${d.weekNum}-${spans.length}`, label: `Sett. ${d.weekNum}`, count: 1, weekNum: d.weekNum });
      });
      return spans;
    }
    days.forEach(d => {
      const last = spans[spans.length - 1];
      if (last && last.month === d.month && last.year === d.year) last.count++;
      else spans.push({ key: `${d.year}-${d.month}-${spans.length}`, label: `${MONTHS_IT[d.month]} ${d.year}`, count: 1, month: d.month, year: d.year });
    });
    return spans;
  }, [days, dateMode]);

  const todayISO = toISO(new Date());
  const chartTitle = selectedChart ? selectedChart.name : 'Cronoprogramma';

  // ---- import excel ----
  const onImportFileChosen = (f) => {
    if (!f) return;
    setChartDialog({ mode: 'import', form: { ...emptyChartForm(), name: f.name.replace(/\.(xlsx|xls|numbers)$/i, '') }, file: f });
    if (importFileRef.current) importFileRef.current.value = '';
  };

  const submitChartDialog = async () => {
    const d = chartDialog;
    if (!d) return;
    if (!d.form.name.trim()) { alert('Il nome del cronoprogramma è obbligatorio'); return; }
    setChartSaving(true);
    try {
      const projectId = d.form.project_id === '__none__' ? null : d.form.project_id;
      if (d.mode === 'import') {
        const fd = new FormData();
        fd.append('file', d.file);
        fd.append('name', d.form.name.trim());
        fd.append('luogo', d.form.luogo || '');
        fd.append('date_mode', d.form.date_mode);
        fd.append('work_saturday', String(!!d.form.work_saturday));
        fd.append('work_sunday', String(!!d.form.work_sunday));
        fd.append('year', String(d.form.year || NOW_YEAR));
        if (projectId) fd.append('project_id', projectId);
        const res = await api.post('/gantt/import-excel', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setChartDialog(null);
        await loadCharts();
        setImportResult(res.data);
        if (res.data?.chart?.id) openChart(res.data.chart);
      } else if (d.mode === 'edit') {
        await api.put(`/gantt/charts/${d.id}`, {
          name: d.form.name.trim(), luogo: d.form.luogo, project_id: projectId,
          date_mode: d.form.date_mode, work_saturday: !!d.form.work_saturday,
          work_sunday: !!d.form.work_sunday, year: Number(d.form.year) || NOW_YEAR,
        });
        setChartDialog(null);
        await loadCharts();
      } else {
        const res = await api.post('/gantt/charts', {
          name: d.form.name.trim(), luogo: d.form.luogo, project_id: projectId,
          date_mode: d.form.date_mode, work_saturday: !!d.form.work_saturday,
          work_sunday: !!d.form.work_sunday, year: Number(d.form.year) || NOW_YEAR,
        });
        setChartDialog(null);
        await loadCharts();
        if (res.data?.id) openChart(res.data);
      }
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setChartSaving(false); }
  };

  const deleteChart = async (chart) => {
    if (!window.confirm(`Eliminare il cronoprogramma "${chart.name}" e tutte le sue attività?`)) return;
    try { await api.delete(`/gantt/charts/${chart.id}`); loadCharts(); }
    catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const exportExcel = async (chart) => {
    try {
      const res = await api.get(`/gantt/charts/${chart.id}/export-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Cronoprogramma_${chart.name.replace(/[^a-zA-Z0-9]+/g, '_')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore export Excel'); }
  };

  const exportPdfServer = async (chart, layout = 'multi', pageSize = 'a4') => {
    try {
      const res = await api.get(`/gantt/charts/${chart.id}/export-pdf`, { params: { layout, page_size: pageSize }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const suffix = layout === 'single' ? `_1pagina_${pageSize.toUpperCase()}` : '';
      a.download = `Cronoprogramma_${chart.name.replace(/[^a-zA-Z0-9]+/g, '_')}${suffix}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { alert('Errore export PDF'); }
  };

  const openApply = () => {
    setApplyForm({ start_date: '', end_date: '', work_saturday: workSaturday, work_sunday: workSunday });
    setApplyDialog(true);
  };
  const submitApply = async (reset = false) => {
    if (!reset && !applyForm.start_date && !applyForm.end_date) { alert('Indica almeno una data di inizio o di fine'); return; }
    setApplying(true);
    try {
      await api.post(`/gantt/charts/${selectedId}/apply-dates`, reset ? { reset: true } : applyForm);
      setApplyDialog(false);
      await loadCharts();
      const res = await api.get(`/gantt/charts/${selectedId}`);
      loadTasks(selectedId, true, res.data);
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setApplying(false); }
  };

  // ---- recap / totali ----
  const totalsByDay = useMemo(() => {
    const map = {};
    days.forEach(d => {
      let v = 0;
      tasks.forEach(t => { if (t.marked_days.includes(d.iso)) v += showWorkers ? (t.num_workers || 0) : 1; });
      map[d.iso] = v;
    });
    return map;
  }, [days, tasks, showWorkers]);

  const recap = useMemo(() => {
    const all = [];
    tasks.forEach(t => t.marked_days.forEach(d => all.push(d)));
    let spanWorking = 0;
    if (all.length) {
      const min = all.reduce((a, b) => a < b ? a : b);
      const max = all.reduce((a, b) => a > b ? a : b);
      let cur = new Date(min + 'T00:00:00');
      const end = new Date(max + 'T00:00:00');
      while (cur <= end) { if (isWorkingDay(cur.getDay())) spanWorking++; cur = new Date(cur.getTime() + DAY); }
    }
    const totalGg = tasks.reduce((s, t) => s + t.marked_days.length, 0);
    const manDays = tasks.reduce((s, t) => s + t.marked_days.length * (t.num_workers || 0), 0);
    const m = Number(margin) || 0;
    return { spanWorking, totalGg, manDays, margin: m, withMargin: spanWorking + m };
  }, [tasks, margin, isWorkingDay]);

  // ---- painting ----
  const applyCell = useCallback((taskId, iso, mode) => {
    setTasks(prev => prev.map(t => {
      if (t.id !== taskId) return t;
      const set = new Set(t.marked_days);
      if (mode === 'add') set.add(iso); else set.delete(iso);
      return { ...t, marked_days: Array.from(set).sort() };
    }));
  }, []);

  const persistTask = useCallback(async (taskId) => {
    const t = tasksRef.current.find(x => x.id === taskId);
    if (!t) return;
    try { await api.put(`/gantt/tasks/${taskId}`, { marked_days: t.marked_days }); }
    catch (e) { alert('Errore salvataggio giornate'); loadTasks(selectedId); }
  }, [selectedId, loadTasks]);

  useEffect(() => {
    const up = () => {
      if (paintRef.current.active) {
        const tid = paintRef.current.taskId;
        paintRef.current = { active: false, taskId: null, mode: 'add' };
        if (tid) persistTask(tid);
      }
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, [persistTask]);

  const onCellDown = (task, iso, e) => {
    if (!canManage) return;
    e.preventDefault();
    const mode = task.marked_days.includes(iso) ? 'remove' : 'add';
    paintRef.current = { active: true, taskId: task.id, mode };
    applyCell(task.id, iso, mode);
  };
  const onCellEnter = (task, iso) => {
    if (!canManage) return;
    const p = paintRef.current;
    if (p.active && p.taskId === task.id) applyCell(task.id, iso, p.mode);
  };

  // ---- task CRUD ----
  const openNew = () => { setEditingId(null); setForm(emptyTaskForm()); setShowForm(true); };
  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({ name: t.name, category: t.category || '', section_id: t.section_id || '', progress: t.progress || 0, num_workers: t.num_workers || 0, color: t.color || COLORS[0], notes: t.notes || '', fillStartMode: 'date', fillStart: '', fillMode: 'durata', fillValue: '' });
    setShowForm(true);
  };
  const save = async () => {
    if (!form.name) { alert('Il nome attività è obbligatorio'); return; }
    const payload = {
      name: form.name, category: form.category, section_id: form.section_id || null, color: form.color, notes: form.notes,
      progress: Number(form.progress), num_workers: Number(form.num_workers),
      chart_id: selectedId,
    };
    if (form.fillStart) {
      const startISO = form.fillStartMode === 'workday' ? nthWorkingDay(form.fillStart) : form.fillStart;
      if (startISO) payload.marked_days = fillDays(startISO, form.fillMode, form.fillValue);
    }
    try {
      if (editingId) await api.put(`/gantt/tasks/${editingId}`, payload);
      else await api.post('/gantt/tasks', payload);
      setShowForm(false); loadTasks(selectedId);
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const [bulkSectionId, setBulkSectionId] = useState('');
  const openBulk = () => { setBulkRows([bulkRow(0), bulkRow(1), bulkRow(2)]); setBulkUseWorkday(false); setBulkSectionId(''); setBulkOpen(true); };
  const setBulkCell = (i, key, val) => setBulkRows(rows => rows.map((r, idx) => idx === i ? { ...r, [key]: val } : r));
  const addBulkRow = () => setBulkRows(rows => [...rows, bulkRow(rows.length)]);
  const removeBulkRow = (i) => setBulkRows(rows => rows.filter((_, idx) => idx !== i));
  const saveBulk = async () => {
    const valid = bulkRows.filter(r => r.name.trim());
    if (!valid.length) { alert('Inserisci almeno una riga con il nome attività'); return; }
    setBulkSaving(true);
    try {
      for (const r of valid) {
        const startISO = bulkUseWorkday ? nthWorkingDay(r.start) : r.start;
        await api.post('/gantt/tasks', {
          name: r.name.trim(), color: r.color, section_id: bulkSectionId || null, num_workers: Number(r.num_workers) || 0,
          chart_id: selectedId,
          marked_days: fillDays(startISO, bulkUseWorkday ? 'durata' : r.mode, r.value),
        });
      }
      setBulkOpen(false); loadTasks(selectedId);
    } catch (e) { alert(e.response?.data?.detail || 'Errore durante il salvataggio multiplo'); }
    finally { setBulkSaving(false); }
  };
  const remove = async (id) => {
    if (!window.confirm('Eliminare questa attività?')) return;
    try { await api.delete(`/gantt/tasks/${id}`); setShowForm(false); loadTasks(selectedId); }
    catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  // riordino manuale: l'elenco resta fisso nell'ordine scelto, non salta più quando cambi le date di una voce
  const moveTask = async (idx, dir) => {
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= tasks.length) return;
    const reordered = [...tasks];
    [reordered[idx], reordered[newIdx]] = [reordered[newIdx], reordered[idx]];
    setTasks(reordered);
    const changed = reordered.map((t, i) => ({ t, newOrder: i + 1 })).filter(({ t, newOrder }) => t.order !== newOrder);
    try {
      await Promise.all(changed.map(({ t, newOrder }) => api.put(`/gantt/tasks/${t.id}`, { order: newOrder })));
    } catch (e) { console.error(e); }
    loadTasks(selectedId);
  };

  const shiftPeriod = (deltaDays) => {
    const d = new Date(periodStart + 'T00:00:00');
    setPeriodStart(toISO(new Date(d.getTime() + deltaDays * DAY)));
  };

  const exportChart = async (type) => {
    if (!tasks.length || exporting) return;
    setExporting(true);
    await new Promise(r => setTimeout(r, 150));
    const node = ganttRef.current;
    try {
      const canvas = await html2canvas(node, { scale: 2, backgroundColor: '#ffffff', windowWidth: node.scrollWidth + 40, scrollX: 0, scrollY: -window.scrollY });
      const fname = `Cronoprogramma_${chartTitle.replace(/[^a-zA-Z0-9]+/g, '_')}_${todayISO}`;
      if (type === 'png') {
        const a = document.createElement('a'); a.href = canvas.toDataURL('image/png'); a.download = `${fname}.png`; a.click();
      } else {
        const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
        const pw = pdf.internal.pageSize.getWidth(), ph = pdf.internal.pageSize.getHeight(), m = 18;
        const availW = pw - m * 2, availH = ph - m * 2;
        // adatta SEMPRE a una singola pagina, mantenendo le proporzioni
        const scale = Math.min(availW / canvas.width, availH / canvas.height);
        const w = canvas.width * scale, h = canvas.height * scale;
        const x = m + Math.max(0, (availW - w) / 2); // centra orizzontalmente
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', x, m, w, h);
        pdf.save(`${fname}.pdf`);
      }
    } catch (e) { alert('Errore export: ' + (e?.message || e)); }
    finally { setExporting(false); }
  };

  const NUM_W = 30, GG_W = 38, OP_W = 38;
  const LEFT_W = NUM_W + descW + GG_W + (showWorkers ? OP_W : 0);
  const ROW_H = 36;

  // altezza riga dinamica: la descrizione va a capo (non più su una riga sola), quindi la riga
  // si allunga in base al testo, e la griglia giorni a destra segue la stessa altezza.
  const measureLines = (text, width, charW) => {
    if (!text) return 0;
    const perLine = Math.max(6, Math.floor((width - 8) / charW));
    return Math.max(1, Math.ceil(text.length / perLine));
  };
  const taskRowHeight = useCallback((t) => {
    const avail = Math.max(60, descW - 34);
    const nameLines = measureLines(t.name, avail, 5.6);
    const catLines = t.category ? measureLines(t.category, avail, 5.0) : 0;
    const h = 12 + nameLines * 15 + (catLines ? catLines * 12 : 0);
    return Math.max(ROW_H, h);
  }, [descW]);

  // ============ VISTA ELENCO CRONOPROGRAMMI ============
  if (!selectedId) {
    return (
      <div className="space-y-4 animate-fade-in" data-testid="gantt-list-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><GanttChartSquare size={24} /> Cronoprogrammi</h1>
            <p className="text-sm text-slate-500">Ogni cronoprogramma è un documento separato (Nome + Luogo) da inviare al cliente. Esportabile in Excel, PDF e PNG.</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <input ref={importFileRef} type="file" accept=".xlsx,.xls,.numbers" className="hidden" onChange={(e) => onImportFileChosen(e.target.files?.[0])} />
              <Button variant="outline" onClick={() => importFileRef.current?.click()} data-testid="import-excel-btn"><Upload size={16} className="mr-1" /> Importa da Excel/Numbers</Button>
              <Button onClick={() => setChartDialog({ mode: 'new', form: emptyChartForm() })} data-testid="new-chart-btn"><Plus size={16} className="mr-1" /> Nuovo Cronoprogramma</Button>
            </div>
          )}
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {chartsLoading ? (
              <div className="p-8 text-center text-slate-400">Caricamento...</div>
            ) : charts.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <GanttChartSquare size={40} className="mx-auto mb-3 text-slate-300" />
                Nessun cronoprogramma. {canManage && 'Creane uno nuovo o importalo da un file Excel/Numbers.'}
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {charts.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`chart-row-${c.id}`}>
                    <button onClick={() => openChart(c)} className="flex-1 text-left min-w-0" data-testid={`open-chart-${c.id}`}>
                      <p className="font-semibold text-slate-800 truncate flex items-center gap-2"><GanttChartSquare size={15} className="text-[#1a4b8c] flex-shrink-0" /> {c.name}</p>
                      <p className="text-xs text-slate-500 flex items-center gap-3 flex-wrap mt-0.5">
                        {c.luogo && <span className="flex items-center gap-1"><MapPin size={12} /> {c.luogo}</span>}
                        {c.project_name && <span className="flex items-center gap-1"><FolderOpen size={12} /> {c.project_name}</span>}
                        <span>{c.task_count || 0} attività</span>
                        <span>{c.date_mode === 'lavorative' ? 'Giornate lavorative' : 'Date esatte'}</span>
                        <span>Anno {c.year}</span>
                      </p>
                    </button>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button size="sm" variant="ghost" onClick={() => openChart(c)} data-testid={`view-chart-${c.id}`}>Apri</Button>
                      <Button size="icon" variant="ghost" title="Esporta Excel" onClick={() => exportExcel(c)} data-testid={`excel-chart-${c.id}`}><FileSpreadsheet size={16} className="text-green-700" /></Button>
                      {canManage && <Button size="icon" variant="ghost" title="Modifica" onClick={() => setChartDialog({ mode: 'edit', id: c.id, form: { name: c.name, luogo: c.luogo || '', project_id: c.project_id || '__none__', date_mode: c.date_mode || 'date', work_saturday: c.work_saturday ?? true, work_sunday: c.work_sunday ?? false, year: c.year || NOW_YEAR } })} data-testid={`edit-chart-${c.id}`}><Pencil size={15} /></Button>}
                      {canManage && <Button size="icon" variant="ghost" title="Elimina" className="text-red-500" onClick={() => deleteChart(c)} data-testid={`delete-chart-${c.id}`}><Trash2 size={15} /></Button>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {renderChartDialog()}
        {renderImportResult()}
      </div>
    );
  }

  // ============ VISTA DETTAGLIO (GRIGLIA) ============
  return (
    <div className="space-y-4 animate-fade-in" data-testid="gantt-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="outline" size="icon" onClick={backToList} data-testid="back-to-list-btn"><ArrowLeft size={16} /></Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2 truncate"><GanttChartSquare size={22} /> {chartTitle}</h1>
            <p className="text-sm text-slate-500 flex items-center gap-3 flex-wrap">
              {selectedChart?.luogo && <span className="flex items-center gap-1"><MapPin size={13} /> {selectedChart.luogo}</span>}
              {selectedChart?.project_name && <span className="flex items-center gap-1"><FolderOpen size={13} /> {selectedChart.project_name}</span>}
              <span>Clicca o trascina sulle caselle per segnare le giornate</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => exportExcel(selectedChart)} data-testid="export-excel-btn"><FileSpreadsheet size={16} className="mr-1 text-green-700" /> Excel</Button>
          <Button variant="outline" onClick={() => setPdfOptionsDialog(true)} disabled={!tasks.length} data-testid="export-pdf-btn"><FileText size={16} className="mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => exportChart('png')} disabled={!tasks.length || exporting} data-testid="export-png-btn"><FileImage size={16} className="mr-1" /> PNG</Button>
          {canManage && (
            <>
              <Button variant="outline" onClick={openApply} data-testid="apply-dates-btn"><CalendarDays size={16} className="mr-1" /> {dateMode === 'lavorative' ? 'Assegna date' : 'Ricalcola date'}</Button>
              <Button variant="outline" onClick={openBulk} data-testid="bulk-add-btn"><Rows3 size={16} className="mr-1" /> Inserimento rapido</Button>
              <Button onClick={openNew} data-testid="new-task-btn"><Plus size={16} className="mr-1" /> Attività</Button>
            </>
          )}
        </div>
      </div>

      {/* Controlli periodo + opzioni */}
      <div className="flex items-end gap-3 flex-wrap bg-white border border-slate-200 rounded-lg p-3">
        <Button variant="outline" size="icon" onClick={() => shiftPeriod(-numDays)} title="Periodo precedente"><ChevronLeft size={16} /></Button>
        <div className="space-y-1"><Label className="text-xs">Inizio periodo</Label><Input type="date" className="w-40" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="period-start" /></div>
        <div className="space-y-1"><Label className="text-xs">N. giorni</Label><Input type="number" min={1} max={180} className="w-20" value={numDays} onChange={(e) => setNumDays(e.target.value)} data-testid="period-days" /></div>
        <Button variant="outline" size="icon" onClick={() => shiftPeriod(numDays)} title="Periodo successivo"><ChevronRight size={16} /></Button>

        <div className="space-y-1"><Label className="text-xs">Visualizzazione</Label>
          <Select value={dateMode} onValueChange={(v) => updateChartSettings({ date_mode: v })} disabled={!canManage}>
            <SelectTrigger className="w-44" data-testid="date-mode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="date">Date esatte</SelectItem>
              <SelectItem value="lavorative">Giornate lavorative</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pb-1"><Checkbox checked={workSaturday} onCheckedChange={(v) => toggleWorkday({ work_saturday: !!v })} disabled={!canManage} data-testid="opt-saturday" /> Lavora il sabato</label>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pb-1"><Checkbox checked={workSunday} onCheckedChange={(v) => toggleWorkday({ work_sunday: !!v })} disabled={!canManage} data-testid="opt-sunday" /> Lavora la domenica</label>
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer pb-1"><Checkbox checked={showWorkers} onCheckedChange={(v) => updateChartSettings({ show_workers: !!v })} disabled={!canManage} data-testid="opt-workers" /> Mostra n° dipendenti</label>
        <div className="space-y-1"><Label className="text-xs">Margine imprevisti (gg)</Label><Input type="number" min={0} className="w-24" value={margin} onChange={(e) => updateChartSettings({ margin: Number(e.target.value) || 0 })} disabled={!canManage} data-testid="opt-margin" /></div>

        <div className="flex items-center gap-1 ml-auto pb-1">
          <Button variant="outline" size="icon" onClick={() => setCellW(w => Math.max(16, w - 4))} title="Riduci"><ZoomOut size={16} /></Button>
          <Button variant="outline" size="icon" onClick={() => setCellW(w => Math.min(60, w + 4))} title="Ingrandisci"><ZoomIn size={16} /></Button>
        </div>
      </div>

      {/* Sezioni/Fasi: registro colori come nei preventivi */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <span className="text-xs font-semibold text-slate-500 mr-1">Sezioni/Fasi:</span>
        {sections.map(s => (
          <div key={s.id} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full border text-xs" style={{ backgroundColor: `${s.color}1a`, borderColor: `${s.color}55` }}>
            <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: s.color }} />
            {canManage ? (
              <input value={s.title} onChange={(e) => renameSection(s.id, e.target.value)} className="h-5 text-xs font-semibold border-none bg-transparent outline-none w-28" style={{ color: s.color }} data-testid={`section-title-${s.id}`} />
            ) : (
              <span className="text-xs font-semibold" style={{ color: s.color }}>{s.title}</span>
            )}
            {canManage && <button onClick={() => deleteSection(s.id)} className="text-red-500 hover:text-red-700 font-bold leading-none ml-0.5" title="Elimina sezione" data-testid={`delete-section-${s.id}`}>&times;</button>}
          </div>
        ))}
        {canManage && (showNewSection ? (
          <div className="inline-flex items-center gap-1">
            <Input value={newSectionTitle} onChange={(e) => setNewSectionTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createSection()} placeholder="Nome fase, es. Tetto galleggiante" className="h-7 text-xs w-48" autoFocus data-testid="new-section-input" />
            <Button size="sm" className="h-7" onClick={createSection} data-testid="confirm-new-section">Aggiungi</Button>
            <Button size="sm" variant="ghost" className="h-7" onClick={() => { setShowNewSection(false); setNewSectionTitle(''); }}>Annulla</Button>
          </div>
        ) : (
          <Button size="sm" variant="outline" className="h-7" onClick={() => setShowNewSection(true)} data-testid="add-section-btn"><Plus size={13} className="mr-1" /> Nuova sezione</Button>
        ))}
        {sections.length === 0 && !showNewSection && <span className="text-xs text-slate-400">Crea una sezione per fase (es. Tetto galleggiante, Fondo) e assegnale un colore, poi collegala alle attività.</span>}
      </div>

      <Card className="border-slate-200 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {tasksLoading ? (
            <div className="p-8 text-center text-slate-400">Caricamento...</div>
          ) : tasks.length === 0 ? (
            <div className="p-10 text-center text-slate-500">Nessuna attività. {canManage && 'Aggiungi la prima attività con il pulsante "Attività" o "Inserimento rapido".'}</div>
          ) : (
            <div ref={ganttRef} className="bg-white select-none">
              {exporting && (
                <div className="px-4 py-3 border-b border-slate-200">
                  <p className="text-base font-bold text-slate-900">Cronoprogramma — {chartTitle}{selectedChart?.luogo ? ` · ${selectedChart.luogo}` : ''}</p>
                  <p className="text-xs text-slate-500">Metal Covering S.r.l. · Generato il {fmtDate(new Date())} · {dateMode === 'lavorative' ? 'Giornate lavorative' : 'Date esatte'}</p>
                </div>
              )}
              <div className="flex">
                {/* Colonne fisse sinistra */}
                <div className="flex-shrink-0 border-r border-slate-300 bg-white z-10" style={{ width: LEFT_W, position: exporting ? 'static' : 'sticky', left: 0 }}>
                  <div className="flex border-b border-slate-300 bg-slate-100" style={{ height: 46 }}>
                    <div className="flex items-center justify-center text-[10px] font-semibold text-slate-500 border-r border-slate-200" style={{ width: NUM_W }}>N°</div>
                    <div className="relative flex items-center px-2 text-[11px] font-semibold text-slate-500 border-r border-slate-200" style={{ width: descW }}>
                      Descrizione attività
                      <div
                        onMouseDown={(e) => {
                          e.preventDefault();
                          const startX = e.clientX; const startW = descW;
                          const onMove = (ev) => setDescW(Math.max(150, Math.min(640, startW + (ev.clientX - startX))));
                          const onUp = () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                          window.addEventListener('mousemove', onMove); window.addEventListener('mouseup', onUp);
                        }}
                        className="absolute right-0 top-0 h-full w-1.5 cursor-col-resize hover:bg-blue-300 active:bg-blue-400"
                        title="Trascina per allargare/restringere"
                        data-testid="desc-col-resize"
                      />
                    </div>
                    <div className="flex items-center justify-center text-[10px] font-semibold text-slate-500" style={{ width: GG_W }}>gg</div>
                    {showWorkers && <div className="flex items-center justify-center text-[10px] font-semibold text-slate-500 border-l border-slate-200" style={{ width: OP_W }} title="Numero dipendenti">op.</div>}
                  </div>
                  {tasks.map((t, idx) => (
                    <div key={t.id} className="group flex border-b border-slate-100 hover:bg-slate-50 cursor-pointer" style={{ height: taskRowHeight(t), borderLeft: t.section_id ? `3px solid ${t.color}` : '3px solid transparent' }} onClick={() => canManage && openEdit(t)} data-testid={`task-label-${t.id}`}>
                      <div className="flex items-center justify-center text-xs font-semibold text-slate-600 border-r border-slate-100" style={{ width: NUM_W }}>{idx + 1}°</div>
                      <div className="relative flex flex-col justify-center px-2 py-1 border-r border-slate-100 overflow-hidden" style={{ width: descW }}>
                        <p className="text-xs font-medium text-slate-800 leading-snug break-words flex items-start gap-1 pr-11" title={t.name}>
                          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0 mt-0.5" style={{ background: t.color }} />{t.name}
                        </p>
                        {t.category ? (<p className="text-[10px] text-slate-400 leading-snug break-words pr-11" title={t.category}>{t.category}</p>) : null}
                        {canManage && !exporting && (
                          <div className="absolute right-1 top-1 flex flex-col items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={(e) => { e.stopPropagation(); moveTask(idx, -1); }} disabled={idx === 0} className="p-0.5 rounded bg-white/90 text-slate-500 hover:bg-slate-100 disabled:opacity-20" title="Sposta su" data-testid={`move-up-${t.id}`}><ChevronUp size={13} /></button>
                            <button onClick={(e) => { e.stopPropagation(); moveTask(idx, 1); }} disabled={idx === tasks.length - 1} className="p-0.5 rounded bg-white/90 text-slate-500 hover:bg-slate-100 disabled:opacity-20" title="Sposta giù" data-testid={`move-down-${t.id}`}><ChevronDown size={13} /></button>
                            <button onClick={(e) => { e.stopPropagation(); remove(t.id); }} className="p-0.5 rounded bg-white/90 text-red-500 hover:bg-red-50" title="Elimina attività" data-testid={`delete-task-${t.id}`}><Trash2 size={13} /></button>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-center text-xs font-semibold text-slate-600" style={{ width: GG_W }}>{t.marked_days?.length ?? 0}</div>
                      {showWorkers && <div className="flex items-center justify-center text-xs text-slate-600 border-l border-slate-100" style={{ width: OP_W }}>{t.num_workers || 0}</div>}
                    </div>
                  ))}
                  {/* riga totali */}
                  <div className="flex border-b-2 border-slate-300 bg-slate-50" style={{ height: ROW_H }}>
                    <div className="flex items-center px-2 text-[11px] font-bold text-slate-600" style={{ width: NUM_W + descW }}>{showWorkers ? 'Tot. dipendenti / giorno' : 'Tot. attività / giorno'}</div>
                    <div className="flex items-center justify-center text-xs font-bold text-slate-700" style={{ width: GG_W }}>{recap.totalGg}</div>
                    {showWorkers && <div className="flex items-center justify-center text-xs font-bold text-slate-700 border-l border-slate-100" style={{ width: OP_W }}>{recap.manDays}</div>}
                  </div>
                </div>

                {/* Griglia giorni */}
                <div className={exporting ? '' : 'overflow-x-auto flex-1'} ref={scrollRef} style={exporting ? { width: days.length * cellW } : undefined}>
                  <div style={{ width: days.length * cellW, minWidth: '100%' }}>
                    <div className="flex border-b border-slate-200 bg-slate-100" style={{ height: 20 }}>
                      {monthSpans.map(ms => (
                        <div key={ms.key} className="text-[10px] font-semibold text-slate-600 border-r border-slate-300 flex items-center px-1 overflow-hidden" style={{ width: ms.count * cellW }}>{ms.label}</div>
                      ))}
                    </div>
                    <div className="flex border-b border-slate-200" style={{ height: 14 }}>
                      {days.map(d => (
                        <div key={d.iso} className={`text-[9px] text-center border-r border-slate-100 leading-[14px] ${!d.working ? 'bg-slate-100 text-slate-400' : 'text-slate-600'} ${d.iso === todayISO ? 'bg-red-100 font-bold text-red-700' : ''}`} style={{ width: cellW }}>{dateMode === 'lavorative' ? (d.workIdx || '') : d.day}</div>
                      ))}
                    </div>
                    <div className="flex border-b border-slate-300" style={{ height: 12 }}>
                      {days.map(d => (
                        <div key={d.iso} className={`text-[8px] text-center border-r border-slate-100 leading-[12px] ${!d.working ? 'bg-slate-100 text-slate-400' : 'text-slate-400'}`} style={{ width: cellW }}>{dateMode === 'lavorative' ? d.lavLetter : WD[d.wd]}</div>
                      ))}
                    </div>
                    {tasks.map(t => {
                      const set = new Set(t.marked_days);
                      const rh = taskRowHeight(t);
                      return (
                        <div key={t.id} className="flex border-b border-slate-100" style={{ height: rh }}>
                          {days.map(d => {
                            const on = set.has(d.iso);
                            return (
                              <div
                                key={d.iso}
                                onMouseDown={(e) => onCellDown(t, d.iso, e)}
                                onMouseEnter={() => onCellEnter(t, d.iso)}
                                className={`border-r border-b border-slate-100 flex items-center justify-center ${canManage ? 'cursor-pointer' : ''} ${!d.working && !on ? 'bg-slate-50' : ''} ${d.iso === todayISO && !on ? 'bg-red-50' : ''}`}
                                style={{ width: cellW, height: rh, background: on ? t.color : undefined }}
                                data-testid={`cell-${t.id}-${d.iso}`}
                                title={`${t.name} · ${fmtDate(d.iso)}`}
                              >
                                {on && showWorkers && (t.num_workers || 0) > 0 && <span className="text-[9px] font-bold text-white leading-none">{t.num_workers}</span>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })}
                    <div className="flex border-b-2 border-slate-300 bg-slate-50" style={{ height: ROW_H }}>
                      {days.map(d => {
                        const v = totalsByDay[d.iso] || 0;
                        return <div key={d.iso} className={`border-r border-slate-100 flex items-center justify-center text-[10px] font-bold ${v > 0 ? 'text-slate-700' : 'text-slate-300'}`} style={{ width: cellW, height: ROW_H }}>{v || ''}</div>;
                      })}
                    </div>
                  </div>
                </div>
              </div>

              {/* Resoconto */}
              <div className="border-t border-slate-200 px-4 py-3 bg-slate-50/60">
                <p className="text-xs font-semibold text-slate-600 mb-2">Resoconto</p>
                <div className="flex flex-wrap gap-3">
                  <Badge variant="outline" className="bg-white">Attività: {tasks.length}</Badge>
                  <Badge variant="outline" className="bg-white">Giornate-attività totali: {recap.totalGg} gg</Badge>
                  {showWorkers && <Badge variant="outline" className="bg-white">Giornate-uomo: {recap.manDays}</Badge>}
                  <Badge variant="outline" className="bg-white">Durata progetto: {recap.spanWorking} gg lavorativi</Badge>
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Margine imprevisti: +{recap.margin} gg</Badge>
                  <Badge className="bg-[#1a4b8c] text-white">Durata stimata con margine: {recap.withMargin} gg</Badge>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog Attività */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Modifica Attività' : 'Nuova Attività'}</DialogTitle>
            <DialogDescription>Definisci nome, n° dipendenti e colore. Le giornate si segnano colorando le caselle nella griglia.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome attività *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="es. Sabbiatura 1° virola interna" data-testid="task-name" /></div>
            <div className="space-y-1">
              <Label>Sezione/Fase</Label>
              <Select value={form.section_id || '__none__'} onValueChange={(v) => {
                if (v === '__none__') { setForm({ ...form, section_id: '' }); return; }
                const sec = sections.find(s => s.id === v);
                setForm({ ...form, section_id: v, category: sec?.title || form.category, color: sec?.color || form.color });
              }}>
                <SelectTrigger data-testid="task-section"><SelectValue placeholder="Nessuna sezione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuna sezione (categoria/colore liberi)</SelectItem>
                  {sections.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {!form.section_id && <div className="space-y-1"><Label>Categoria/Fase</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="es. Sabbiatura" /></div>}
              <div className="space-y-1"><Label>N° dipendenti</Label><Input type="number" min={0} value={form.num_workers} onChange={(e) => setForm({ ...form, num_workers: e.target.value })} data-testid="task-workers" /></div>
              <div className="space-y-1"><Label>Avanzamento %</Label><Input type="number" min={0} max={100} value={form.progress} onChange={(e) => setForm({ ...form, progress: e.target.value })} data-testid="task-progress" /></div>
            </div>
            {!form.section_id && (
              <div className="space-y-1">
                <Label>Colore</Label>
                <div className="flex gap-2 flex-wrap">
                  {COLORS.map(c => <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} className={`w-7 h-7 rounded-md border-2 ${form.color === c ? 'border-slate-900 scale-110' : 'border-transparent'} transition-transform`} style={{ background: c }} />)}
                </div>
              </div>
            )}
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2">
              <Label className="text-xs font-semibold text-slate-600">Riempimento rapido giornate {editingId && <span className="font-normal text-slate-400">(lascia vuoto per non modificare le caselle già segnate)</span>}</Label>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1"><Label className="text-[10px]">Inizio per</Label>
                  <Select value={form.fillStartMode} onValueChange={(v) => setForm({ ...form, fillStartMode: v, fillStart: '', fillMode: v === 'workday' ? 'durata' : form.fillMode, fillValue: '' })}>
                    <SelectTrigger data-testid="fill-start-mode"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="date">Data precisa</SelectItem><SelectItem value="workday">Giornata lavorativa n°</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-[10px]">{form.fillStartMode === 'workday' ? 'Giornata lav. n° (da inizio periodo)' : 'Data inizio'}</Label>
                  {form.fillStartMode === 'workday'
                    ? <Input type="number" min={1} value={form.fillStart} onChange={(e) => setForm({ ...form, fillStart: e.target.value })} data-testid="fill-start" />
                    : <Input type="date" value={form.fillStart} onChange={(e) => setForm({ ...form, fillStart: e.target.value })} data-testid="fill-start" />}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {form.fillStartMode === 'date' && (
                  <div className="space-y-1"><Label className="text-[10px]">Tipo</Label>
                    <Select value={form.fillMode} onValueChange={(v) => setForm({ ...form, fillMode: v, fillValue: '' })}>
                      <SelectTrigger data-testid="fill-mode"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="durata">Durata (gg)</SelectItem><SelectItem value="datafine">Data fine</SelectItem></SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1"><Label className="text-[10px]">{form.fillMode === 'durata' ? 'N° giorni' : 'Data fine'}</Label>
                  {form.fillMode === 'durata'
                    ? <Input type="number" min={0} value={form.fillValue} onChange={(e) => setForm({ ...form, fillValue: e.target.value })} data-testid="fill-value" />
                    : <Input type="date" value={form.fillValue} onChange={(e) => setForm({ ...form, fillValue: e.target.value })} data-testid="fill-value" />}
                </div>
              </div>
              <p className="text-[10px] text-slate-400">Salta i giorni non lavorativi{!workSaturday ? ', sabati' : ''}{!workSunday ? ' e domeniche' : ''}.</p>
            </div>
            <div className="space-y-1"><Label>Note</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter className="gap-2">
            {editingId && canManage && <Button variant="ghost" className="text-red-600 mr-auto" onClick={() => remove(editingId)}><Trash2 size={14} className="mr-1" /> Elimina</Button>}
            <Button variant="outline" onClick={() => setShowForm(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-task-btn">{editingId ? 'Aggiorna' : 'Salva'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Inserimento rapido */}
      <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Inserimento rapido attività</DialogTitle>
            <DialogDescription>Aggiungi più attività insieme. Le giornate vengono colorate automaticamente saltando i giorni non lavorativi.</DialogDescription>
          </DialogHeader>
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mb-2"><Checkbox checked={bulkUseWorkday} onCheckedChange={(v) => { setBulkUseWorkday(!!v); setBulkRows(rows => rows.map(r => ({ ...r, start: '', mode: 'durata', value: '' }))); }} data-testid="bulk-workday" /> Inserisci per <strong>giornate lavorative (n°)</strong> invece delle date precise (a partire dall'inizio periodo)</label>

          {sections.length > 0 && (
            <div className="mb-2 space-y-1">
              <Label className="text-xs">Sezione/Fase (applicata a tutte le righe qui sotto)</Label>
              <Select value={bulkSectionId || '__none__'} onValueChange={(v) => setBulkSectionId(v === '__none__' ? '' : v)}>
                <SelectTrigger className="w-64" data-testid="bulk-section"><SelectValue placeholder="Nessuna sezione" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Nessuna sezione (colore per riga)</SelectItem>
                  {sections.map(s => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="hidden md:grid grid-cols-[1.4fr_120px_70px_140px_110px_120px_32px] gap-2 px-1 pb-1 text-[10px] font-semibold text-slate-500">
            <div>Nome attività</div><div>Colore</div><div>N° dip.</div><div>{bulkUseWorkday ? 'G. lav. inizio n°' : 'Data inizio'}</div><div>{bulkUseWorkday ? '' : 'Tipo'}</div><div>{bulkUseWorkday ? 'Durata (gg)' : 'Durata/Data fine'}</div><div></div>
          </div>
          <div className="space-y-2">
            {bulkRows.map((r, i) => (
              <div key={i} className="grid grid-cols-2 md:grid-cols-[1.4fr_120px_70px_140px_110px_120px_32px] gap-2 items-center border-b border-slate-100 pb-2 md:pb-0 md:border-0">
                <Input placeholder="Nome attività" value={r.name} onChange={(e) => setBulkCell(i, 'name', e.target.value)} data-testid={`bulk-name-${i}`} />
                {bulkSectionId ? (
                  <div className="text-[10px] text-slate-400 self-center">Colore da sezione</div>
                ) : (
                  <div className="flex gap-1 flex-wrap">
                    {COLORS.map(c => <button key={c} type="button" onClick={() => setBulkCell(i, 'color', c)} className={`w-5 h-5 rounded ${r.color === c ? 'ring-2 ring-offset-1 ring-slate-900' : ''}`} style={{ background: c }} />)}
                  </div>
                )}
                <Input type="number" min={0} value={r.num_workers} onChange={(e) => setBulkCell(i, 'num_workers', e.target.value)} />
                {bulkUseWorkday
                  ? <Input type="number" min={1} placeholder="n°" value={r.start} onChange={(e) => setBulkCell(i, 'start', e.target.value)} data-testid={`bulk-start-${i}`} />
                  : <Input type="date" value={r.start} onChange={(e) => setBulkCell(i, 'start', e.target.value)} data-testid={`bulk-start-${i}`} />}
                {bulkUseWorkday
                  ? <div className="text-[10px] text-slate-400 self-center hidden md:block">Durata gg</div>
                  : (
                    <Select value={r.mode} onValueChange={(v) => setBulkCell(i, 'mode', v)}>
                      <SelectTrigger data-testid={`bulk-mode-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="durata">Durata gg</SelectItem><SelectItem value="datafine">Data fine</SelectItem></SelectContent>
                    </Select>
                  )}
                {(bulkUseWorkday || r.mode === 'durata')
                  ? <Input type="number" min={0} placeholder="gg" value={r.value} onChange={(e) => setBulkCell(i, 'value', e.target.value)} data-testid={`bulk-value-${i}`} />
                  : <Input type="date" value={r.value} onChange={(e) => setBulkCell(i, 'value', e.target.value)} data-testid={`bulk-value-${i}`} />}
                <Button size="icon" variant="ghost" className="text-red-500 h-8 w-8" onClick={() => removeBulkRow(i)}><X size={14} /></Button>
              </div>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={addBulkRow} className="mt-2 w-fit" data-testid="bulk-add-row"><Plus size={14} className="mr-1" /> Aggiungi riga</Button>

          <DialogFooter className="gap-2 mt-2">
            <Button variant="outline" onClick={() => setBulkOpen(false)}>Annulla</Button>
            <Button onClick={saveBulk} disabled={bulkSaving} data-testid="bulk-save-btn">{bulkSaving ? 'Salvataggio...' : 'Salva tutte'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assegna date calendario */}
      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assegna date di calendario</DialogTitle>
            <DialogDescription>
              Indica una data di inizio (e/o fine) e se si lavora sabato/domenica: le giornate lavorative verranno posizionate sulle date esatte del calendario. Senza date, il cronoprogramma resta in giornate lavorative.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Data inizio</Label><Input type="date" value={applyForm.start_date} onChange={(e) => setApplyForm({ ...applyForm, start_date: e.target.value })} data-testid="apply-start-date" /></div>
              <div className="space-y-1"><Label>Data fine (opz.)</Label><Input type="date" value={applyForm.end_date} onChange={(e) => setApplyForm({ ...applyForm, end_date: e.target.value })} data-testid="apply-end-date" /></div>
            </div>
            <p className="text-[11px] text-slate-400">Se indichi solo la data fine, l'ultima giornata verrà ancorata a quella data (calcolo a ritroso).</p>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Checkbox checked={applyForm.work_saturday} onCheckedChange={(v) => setApplyForm({ ...applyForm, work_saturday: !!v })} data-testid="apply-saturday" /> Lavora il sabato</label>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Checkbox checked={applyForm.work_sunday} onCheckedChange={(v) => setApplyForm({ ...applyForm, work_sunday: !!v })} data-testid="apply-sunday" /> Lavora la domenica</label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            {dateMode === 'date' && <Button variant="ghost" className="mr-auto text-slate-500" onClick={() => submitApply(true)} disabled={applying} data-testid="apply-reset-btn">Torna a giornate lavorative</Button>}
            <Button variant="outline" onClick={() => setApplyDialog(false)}>Annulla</Button>
            <Button onClick={() => submitApply(false)} disabled={applying} data-testid="apply-confirm-btn">{applying ? 'Applico...' : 'Applica date'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pdfOptionsDialog} onOpenChange={setPdfOptionsDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Esporta PDF</DialogTitle>
            <DialogDescription>Scegli come impaginare il cronoprogramma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Layout</Label>
              <Select value={pdfLayout} onValueChange={setPdfLayout}>
                <SelectTrigger data-testid="pdf-layout-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="multi">Multipagina (leggibile, consigliato)</SelectItem>
                  <SelectItem value="single">Pagina singola (tutto compresso)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400">
                {pdfLayout === 'multi'
                  ? 'Divide le settimane su più pagine A4 per restare leggibile.'
                  : 'Tutte le colonne-giorno su un unico foglio: con molte settimane le celle diventano piccole. Eventuali spazi vuoti in basso sono normali.'}
              </p>
            </div>
            {pdfLayout === 'single' && (
              <div className="space-y-1">
                <Label>Formato foglio</Label>
                <Select value={pdfPageSize} onValueChange={setPdfPageSize}>
                  <SelectTrigger data-testid="pdf-pagesize-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="a4">A4</SelectItem>
                    <SelectItem value="a3">A3 (più grande, celle più leggibili)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPdfOptionsDialog(false)}>Annulla</Button>
            <Button onClick={() => { exportPdfServer(selectedChart, pdfLayout, pdfPageSize); setPdfOptionsDialog(false); }} data-testid="pdf-options-confirm">Scarica PDF</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {renderChartDialog()}
      {renderImportResult()}
    </div>
  );

  // ---- dialog crea/modifica/import chart ----
  function renderChartDialog() {
    const d = chartDialog;
    if (!d) return null;
    const isImport = d.mode === 'import';
    const title = isImport ? 'Importa Cronoprogramma da Excel/Numbers' : d.mode === 'edit' ? 'Modifica Cronoprogramma' : 'Nuovo Cronoprogramma';
    return (
      <Dialog open={!!chartDialog} onOpenChange={(o) => !o && setChartDialog(null)}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {isImport ? `File: ${d.file?.name}. Indica nome, luogo e modalità di lettura prima di importare.` : 'Compila i dati del documento da inviare al cliente.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Nome cronoprogramma *</Label><Input value={d.form.name} onChange={(e) => setChartDialog({ ...d, form: { ...d.form, name: e.target.value } })} placeholder="es. Manutenzione Serbatoio T-101" data-testid="chart-name" /></div>
            <div className="space-y-1"><Label>Luogo</Label><Input value={d.form.luogo} onChange={(e) => setChartDialog({ ...d, form: { ...d.form, luogo: e.target.value } })} placeholder="es. Raffineria Sannazzaro" data-testid="chart-luogo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label>Progetto (opzionale)</Label>
                <Select value={d.form.project_id} onValueChange={(v) => setChartDialog({ ...d, form: { ...d.form, project_id: v } })}>
                  <SelectTrigger data-testid="chart-project"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="__none__">Nessuno</SelectItem>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1"><Label>Anno</Label><Input type="number" value={d.form.year} onChange={(e) => setChartDialog({ ...d, form: { ...d.form, year: e.target.value } })} data-testid="chart-year" /></div>
            </div>
            <div className="space-y-1"><Label>Modalità inserimento date</Label>
              <Select value={d.form.date_mode} onValueChange={(v) => setChartDialog({ ...d, form: { ...d.form, date_mode: v } })}>
                <SelectTrigger data-testid="chart-date-mode"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="date">Data precisa (calendario)</SelectItem>
                  <SelectItem value="lavorative">Giornate lavorative (giorno 1, 2, 3...)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Checkbox checked={d.form.work_saturday} onCheckedChange={(v) => setChartDialog({ ...d, form: { ...d.form, work_saturday: !!v } })} data-testid="chart-saturday" /> Lavora il sabato</label>
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer"><Checkbox checked={d.form.work_sunday} onCheckedChange={(v) => setChartDialog({ ...d, form: { ...d.form, work_sunday: !!v } })} data-testid="chart-sunday" /> Lavora la domenica</label>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setChartDialog(null)}>Annulla</Button>
            <Button onClick={submitChartDialog} disabled={chartSaving} data-testid="chart-save-btn">{chartSaving ? 'Attendere...' : (isImport ? 'Importa' : d.mode === 'edit' ? 'Salva modifiche' : 'Crea')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // ---- risultato import ----
  function renderImportResult() {
    return (
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Import completato</DialogTitle>
            <DialogDescription>Attività importate nel cronoprogramma "{importResult?.chart?.name}".</DialogDescription>
          </DialogHeader>
          {importResult && (
            <div className="text-sm space-y-2">
              <p className="text-green-700 font-semibold">{importResult.created_count} attività importate</p>
              <div className="border rounded divide-y max-h-60 overflow-y-auto">
                {importResult.created?.map((c, i) => (
                  <div key={i} className="px-3 py-1.5 flex justify-between"><span className="truncate mr-2">{c.name}</span><span className="text-slate-500 whitespace-nowrap">{c.giorni} gg</span></div>
                ))}
              </div>
              {importResult.warnings?.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs text-amber-700">
                  {importResult.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    );
  }
}
